import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../helpers/common'

export const SalesLoadingKeys = {
    getShopifyAmazonSales: "GET_SHOPIFY_AMAZON_SALES",
    getShopifyAmazonSalesCalculation: "GET_SHOPIFY_AMAZON_SALES_CALCULATION"
}

const initialState = {
    amazonFba: null,
    loading: false,
    loadingKeys: [],
    error: null,
    loadingExtraOrders: [],
    isFirstLoadComplete: false,
    pagination: {
        isTheLastPage: false,
        rangeRows: "",
        totalPages: 0
    },
    sales: [],
    shopifyAmazonSales: [],
    shopifyAmazonPagination: {
        isTheLastPage: false,
        rangeRows: "",
        totalPages: 0
    },
    currentDemandForecastPlanningId: null,
    currentDemandForecastYear: null,
}

export const getShopifyAmazonSalesCalculation = createAsyncThunk(
  'inventory/getShopifyAmazonSalesCalculation',
  async ({
    search,
    phasedStatuses,
    page, 
    isNew,
    itemsPerPage,
    showStockable,
    skipVelocity,
    isReplenishable,
    categories,
    activeFilter,
    sortDirection,
    sortBy,
    demandBasisType,
    trailingPeriod,
    customStartDate,
    customEndDate,
    quarter,
    year,
    leaveOnlySoldOut,
    channel
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/shopify-amazon-sales-calculation`, { params: { 
        search, 
        page, 
        isNew,
        itemsPerPage, 
        activeFilter,
        showStockable,
        skipVelocity,
        isReplenishable,
        categories,
        phasedStatuses,
        sortDirection,
        sortBy,
        demandBasisType,
        trailingPeriod,
        customStartDate,
        customEndDate,
        quarter,
        year,
        leaveOnlySoldOut,
        channel
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getShopifyAmazonSales = createAsyncThunk(
  'inventory/getShopifyAmazonSales',
  async ({
    search,
    phasedStatuses,
    page, 
    isNew,
    itemsPerPage,
    showStockable,
    isReplenishable,
    categories,
    activeFilter,
    sortDirection,
    sortBy
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/shopify-amazon-sales-by-sku`, { params: { 
        search, 
        page, 
        isNew,
        itemsPerPage, 
        activeFilter,
        showStockable,
        isReplenishable,
        categories,
        phasedStatuses,
        sortDirection,
        sortBy
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const inventorySlice = createSlice({
    name: 'inventory',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(getShopifyAmazonSales.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, SalesLoadingKeys.getShopifyAmazonSales];
        })
        .addCase(getShopifyAmazonSales.fulfilled, (state, action) => {
            state.loading = false;
            state.shopifyAmazonSales = action.payload?.items;
            state.error = null;
            state.isFirstLoadComplete = true;
            state.shopifyAmazonPagination.isTheLastPage = action.payload?.isTheLastPage;
            state.shopifyAmazonPagination.rangeRows = action.payload?.rangeRows;
            state.shopifyAmazonPagination.totalPages = action.payload?.totalPages;
            state.loadingKeys = removeLoadingKey(SalesLoadingKeys.getShopifyAmazonSales, state.loadingKeys);
        })
        .addCase(getShopifyAmazonSales.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.isFirstLoadComplete = true;
            state.shopifyAmazonPagination.isTheLastPage = false;
            state.shopifyAmazonPagination.rangeRows = "";
            state.shopifyAmazonPagination.totalPages = 0;
            state.loadingKeys = removeLoadingKey(SalesLoadingKeys.getShopifyAmazonSales, state.loadingKeys);
        })
        .addCase(getShopifyAmazonSalesCalculation.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, SalesLoadingKeys.getShopifyAmazonSalesCalculation];
        })
        .addCase(getShopifyAmazonSalesCalculation.fulfilled, (state, action) => {
            state.loading = false;
            state.shopifyAmazonSales = action.payload?.items;
            state.error = null;
            state.isFirstLoadComplete = true;
            state.shopifyAmazonPagination.isTheLastPage = action.payload?.isTheLastPage;
            state.shopifyAmazonPagination.rangeRows = action.payload?.rangeRows;
            state.shopifyAmazonPagination.totalPages = action.payload?.totalPages;
            state.currentDemandForecastPlanningId = action.payload?.currentDemandForecastPlanningId ?? null;
            state.currentDemandForecastYear = action.payload?.currentDemandForecastYear ?? null;
            state.amazonFba = action.payload?.amazonFba ?? null;
            state.loadingKeys = removeLoadingKey(SalesLoadingKeys.getShopifyAmazonSalesCalculation, state.loadingKeys);
        })
        .addCase(getShopifyAmazonSalesCalculation.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.isFirstLoadComplete = true;
            state.shopifyAmazonPagination.isTheLastPage = false;
            state.shopifyAmazonPagination.rangeRows = "";
            state.shopifyAmazonPagination.totalPages = 0;
            state.currentDemandForecastPlanningId = null;
            state.currentDemandForecastYear = null;
            state.amazonFba = null;
            state.loadingKeys = removeLoadingKey(SalesLoadingKeys.getShopifyAmazonSalesCalculation, state.loadingKeys);
        })
    }
})

export const {} = inventorySlice.actions;

export default inventorySlice.reducer